import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Chip,
    Grid,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Checkbox,
    TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PrintIcon from "@mui/icons-material/Print";
import {
    honorariosSolicitudDetalleJWT,
    honorariosConsultaJWT,
    honorariosSolicitudCerrarJWT,
    honorariosSolicitudAnularJWT,
    honorariosSolicitudAnadirJWT,
    honorariosSolicitudQuitarJWT,
} from "../../api/endpoints";

const getEstadoColor = (idEstado) => {
    switch (idEstado) {
        case 3: // Borrador
            return "primary";
        case 4: // Cerrada
            return "default";
        case 0: // Pendiente
            return "secondary";
        case 2: // En Curso
            return "secondary";
        case 1: // Pagada
            return "primary";
        case -1: // Anulada
            return "default";
        default:
            return "default";
    }
};

const SolicitudDetalle = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [solicitud, setSolicitud] = useState(null);
    const [certificados, setCertificados] = useState([]);
    const [error, setError] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [anularDialog, setAnularDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });

    // Certificate selection modal
    const [modalOpen, setModalOpen] = useState(false);
    const [availableCertificados, setAvailableCertificados] = useState([]);
    const [selectedToAdd, setSelectedToAdd] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Filtros de fecha para búsqueda de certificados (por defecto último año)
    const getDefaultFechaDesde = () => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split('T')[0];
    };
    const getDefaultFechaHasta = () => {
        return new Date().toISOString().split('T')[0];
    };
    const [fechaDesde, setFechaDesde] = useState(getDefaultFechaDesde());
    const [fechaHasta, setFechaHasta] = useState(getDefaultFechaHasta());

    // Certificate removal
    const [selectedToRemove, setSelectedToRemove] = useState([]);

    useEffect(() => {
        fetchDetalle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchDetalle = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await honorariosSolicitudDetalleJWT(id);
            if (response.ok) {
                setSolicitud(response.solicitud);
                setCertificados(response.certificados || []);
            } else {
                setError(response.mensaje || "Error al cargar el detalle");
            }
        } catch (err) {
            // Re-throw SESSION_EXPIRED to trigger modal
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setError(err?.message || "Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate("/honorarios", { state: { activeTab: 2 } });
    };

    const handleOpenConfirmDialog = () => {
        setConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialog(false);
    };

    const handleCerrarSolicitud = async () => {
        setProcessing(true);
        setConfirmDialog(false);
        try {
            const response = await honorariosSolicitudCerrarJWT(parseInt(id));
            if (response.ok) {
                setNotification({ open: true, message: "Solicitud cerrada correctamente", severity: "success" });
                // Refresh data to show updated state
                await fetchDetalle();
            } else {
                setNotification({ open: true, message: response.mensaje || "Error al cerrar solicitud", severity: "error" });
            }
        } catch (err) {
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setNotification({ open: true, message: err?.message || "Error de conexión", severity: "error" });
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const handleOpenModal = async () => {
        setModalOpen(true);
        buscarCertificados();
    };

    const buscarCertificados = async () => {
        setModalLoading(true);
        try {
            const response = await honorariosConsultaJWT(fechaDesde, fechaHasta, "");
            if (response.ok) {
                setAvailableCertificados(response.data || []);
                if ((response.data || []).length === 0) {
                    setNotification({
                        open: true,
                        message: "No se encontraron certificados en el rango de fechas seleccionado",
                        severity: "info"
                    });
                }
            }
        } catch (err) {
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setNotification({
                open: true,
                message: "Error al buscar certificados",
                severity: "error"
            });
        } finally {
            setModalLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedToAdd([]);
        setSearchTerm("");
    };

    const handleToggleAdd = (certId) => {
        setSelectedToAdd(prev =>
            prev.includes(certId)
                ? prev.filter(id => id !== certId)
                : [...prev, certId]
        );
    };

    const handleToggleRemove = (certId) => {
        setSelectedToRemove(prev =>
            prev.includes(certId)
                ? prev.filter(id => id !== certId)
                : [...prev, certId]
        );
    };

    const handleAñadirCertificados = async () => {
        if (selectedToAdd.length === 0) {
            setNotification({ open: true, message: "Seleccione al menos un certificado", severity: "warning" });
            return;
        }

        setProcessing(true);
        try {
            const response = await honorariosSolicitudAnadirJWT(parseInt(id), selectedToAdd);
            if (response.ok) {
                setNotification({ open: true, message: response.mensaje || "Certificados añadidos correctamente", severity: "success" });
                handleCloseModal();
                await fetchDetalle();
            } else {
                setNotification({ open: true, message: response.mensaje || "Error al añadir certificados", severity: "error" });
            }
        } catch (err) {
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setNotification({ open: true, message: err?.message || "Error de conexión", severity: "error" });
        } finally {
            setProcessing(false);
        }
    };

    const handleQuitarCertificados = async () => {
        if (selectedToRemove.length === 0) {
            setNotification({ open: true, message: "Seleccione al menos un certificado para quitar", severity: "warning" });
            return;
        }

        setProcessing(true);
        try {
            const response = await honorariosSolicitudQuitarJWT(parseInt(id), selectedToRemove);
            if (response.ok) {
                setNotification({ open: true, message: response.mensaje || "Certificados quitados correctamente", severity: "success" });
                setSelectedToRemove([]);
                await fetchDetalle();
            } else {
                setNotification({ open: true, message: response.mensaje || "Error al quitar certificados", severity: "error" });
            }
        } catch (err) {
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setNotification({ open: true, message: err?.message || "Error de conexión", severity: "error" });
        } finally {
            setProcessing(false);
        }
    };

    const handleImprimir = () => {
        const url = `/honorarios/solicitud/${id}/imprimir`;
        const iframeId = "print-iframe-solicitud";
        let iframe = document.getElementById(iframeId);

        if (!iframe) {
            iframe = document.createElement("iframe");
            iframe.id = iframeId;
            iframe.style.position = "fixed";
            iframe.style.top = "-9999px";
            iframe.style.left = "-9999px";
            iframe.style.width = "0px";
            iframe.style.height = "0px";
            document.body.appendChild(iframe);
        }

        iframe.src = url;
    };

    const handleOpenAnularDialog = () => setAnularDialog(true);
    const handleCloseAnularDialog = () => setAnularDialog(false);

    const handleAnularSolicitud = async () => {
        setProcessing(true);
        try {
            const response = await honorariosSolicitudAnularJWT(parseInt(id));
            if (response.ok) {
                setNotification({ open: true, message: "Solicitud anulada correctamente", severity: "success" });
                handleCloseAnularDialog();
                setTimeout(() => navigate("/honorarios"), 1000);
            } else {
                setNotification({ open: true, message: response.mensaje || "Error al anular solicitud", severity: "error" });
                handleCloseAnularDialog();
            }
        } catch (err) {
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setNotification({ open: true, message: err?.message || "Error de conexión", severity: "error" });
            handleCloseAnularDialog();
        } finally {
            setProcessing(false);
        }
    };


    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                width: "100%",
                maxWidth: "clamp(0px, 100%, 1100px)",
                margin: "0 auto",
                p: 1,
                boxSizing: "border-box",
                overflowX: "hidden",
                minWidth: 0,
            }}>
                <Typography color="error" variant="h6">
                    {error}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    style={{ marginTop: 16 }}
                >
                    Volver
                </Button>
            </Box>
        );
    }

    if (!solicitud) {
        return null;
    }

    return (
        <Box sx={{
            width: "100%",
            maxWidth: "clamp(0px, 100%, 1100px)",
            margin: "0 auto",
            p: 1,
            boxSizing: "border-box",
            overflowX: "hidden",
            minWidth: 0,
        }}>
            {/* Botón Volver */}
            <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                    mb: 2,
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                }}
            >
                Volver a Solicitudes
            </Button>
            {/* Encabezado de Solicitud */}
            <Paper
                elevation={3}
                sx={{
                    mb: 3,
                    p: { xs: 2, md: 3 },
                    overflowX: "auto",
                    boxSizing: "border-box",
                    minWidth: 0,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                    }}
                >
                    Solicitud de Pago Nº {solicitud.Id}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            Fecha
                        </Typography>
                        <Typography variant="body1" sx={{ color: "text.primary", fontSize: { xs: "0.875rem", md: "1rem" }, wordBreak: "break-word" }}>
                            {solicitud.Fecha}
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            Estado
                        </Typography>
                        <Chip
                            label={solicitud.Estado}
                            color={getEstadoColor(solicitud.Id_Estado)}
                            sx={{ fontWeight: "bold" }}
                            size="small"
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            Total Honorarios
                        </Typography>
                        <Typography variant="h6" color="primary">
                            $ {solicitud.Honorarios.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            Certificados
                        </Typography>
                        <Typography variant="body1" sx={{ color: "text.primary", fontSize: { xs: "0.875rem", md: "1rem" }, wordBreak: "break-word" }}>
                            {certificados.length}
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            Profesional
                        </Typography>
                        <Typography variant="body1" sx={{ color: "text.primary", fontSize: { xs: "0.875rem", md: "1rem" }, wordBreak: "break-word" }}>
                            {solicitud.Profesional} - Mat: {solicitud.Matricula_Provincial}
                        </Typography>
                    </Grid>

                    {solicitud.Expediente_Adm && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                                Expediente Administrativo
                            </Typography>
                            <Typography variant="body1" sx={{ color: "text.primary", fontSize: { xs: "0.875rem", md: "1rem" }, wordBreak: "break-word" }}>
                                {solicitud.Expediente_Adm}
                            </Typography>
                        </Grid>
                    )}

                    {solicitud.Fecha_Pago && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                                Fecha de Pago
                            </Typography>
                            <Typography variant="body1" color="primary">
                                {solicitud.Fecha_Pago}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
            {/* Botones de Edición (solo para Borrador) */}
            {solicitud.Id_Estado === 3 && !solicitud.Expediente_Adm && (
                <Box sx={{ mt: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenModal}
                        disabled={processing}
                        size="small"
                    >
                        Añadir Certificados
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={handleQuitarCertificados}
                        disabled={processing || selectedToRemove.length === 0}
                        style={{ backgroundColor: "#fff", color: "#333" }}
                        size="small"
                    >
                        Quitar ({selectedToRemove.length})
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CheckIcon />}
                        onClick={handleOpenConfirmDialog}
                        disabled={processing}
                        size="small"
                    >
                        {processing ? "Cerrando..." : "Cerrar Solicitud"}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DeleteForeverIcon />}
                        onClick={handleOpenAnularDialog}
                        disabled={processing}
                        style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                        size="small"
                    >
                        Anular
                    </Button>
                </Box>
            )}
            <Divider style={{ margin: "24px 0" }} />
            {/* Tabla de Certificados */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }} mb={1}>
                <Typography variant="h6" style={{ color: 'white' }}>
                    Certificados Incluidos ({certificados.length})
                </Typography>
                <Button variant="contained" startIcon={<PrintIcon />} onClick={handleImprimir}>
                    Imprimir
                </Button>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 3, overflowX: "auto", maxWidth: "100%", width: "100%", boxSizing: "border-box" }}>
                <Table size="small" sx={{ tableLayout: "fixed", width: "100%", wordBreak: "break-word" }}>
                    <TableHead>
                        <TableRow>
                            {solicitud.Id_Estado === 3 && !solicitud.Expediente_Adm && (
                                <TableCell padding="checkbox"></TableCell>
                            )}
                            <TableCell>Fecha Plan</TableCell>
                            <TableCell>Fecha Inicio</TableCell>
                            <TableCell>Autos</TableCell>
                            <TableCell>Juzgado</TableCell>
                            <TableCell>Nro Plan</TableCell>
                            <TableCell>Serie</TableCell>
                            <TableCell>Certif</TableCell>
                            <TableCell align="right">Deuda</TableCell>
                            <TableCell align="right">Costas</TableCell>
                            <TableCell align="right">IVA</TableCell>
                            <TableCell>Demandado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {certificados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={solicitud.Id_Estado === 3 ? 12 : 11} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No hay certificados en esta solicitud
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            certificados.map((cert, idx) => (
                                <TableRow key={cert.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                    {solicitud.Id_Estado === 3 && !solicitud.Expediente_Adm && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedToRemove.includes(cert.Id)}
                                                onChange={() => handleToggleRemove(cert.Id)}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>{cert.FPago}</TableCell>
                                    <TableCell>{cert.Fecha_Inicio}</TableCell>
                                    <TableCell>{cert.Expediente_Autos}</TableCell>
                                    <TableCell>{cert.Expediente_Radicacion}</TableCell>
                                    <TableCell>{cert.Ref_Nro_Plan}</TableCell>
                                    <TableCell>{cert.Letra}</TableCell>
                                    <TableCell>{cert.Numero}</TableCell>
                                    <TableCell align="right">
                                        $ {parseFloat(cert.Deuda_Certificado).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell align="right">
                                        $ {parseFloat(cert.Costas).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell align="right">
                                        $ {parseFloat(cert.Costas_IVA).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell>{cert.Demandado_NN}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog} onClose={handleCloseConfirmDialog}>
                <DialogTitle>Confirmar Cierre de Solicitud</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea cerrar la solicitud #{solicitud.Id}?
                        <br /><br />
                        Una vez cerrada, no podrá agregar ni quitar certificados.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>
                        Cancelar
                    </Button>
                    <Button onClick={handleCerrarSolicitud} color="primary" variant="contained">
                        Cerrar Solicitud
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Confirmation Dialog for Anular */}
            <Dialog open={anularDialog} onClose={handleCloseAnularDialog}>
                <DialogTitle>Confirmar Anulación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea <strong style={{ color: "#d32f2f" }}>ANULAR DEFINITIVAMENTE</strong> la solicitud #{solicitud.Id}?
                        <br /><br />
                        Esta acción liberará todos los certificados incluidos para que puedan ser usados en una nueva solicitud.
                        <br />
                        <strong>Esta acción no se puede deshacer.</strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAnularDialog}>
                        Cancelar
                    </Button>
                    <Button onClick={handleAnularSolicitud} style={{ backgroundColor: "#d32f2f", color: "#fff" }} variant="contained">
                        Anular Solicitud
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal for Adding Certificates */}
            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Añadir Certificados a la Solicitud
                    {/* Filtros de fecha */}
                    <Box display="flex" gap={2} mt={2} mb={1} alignItems="center" flexWrap="wrap">
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            value={fechaDesde}
                            onChange={(e) => setFechaDesde(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            style={{ minWidth: 150 }}
                        />
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            value={fechaHasta}
                            onChange={(e) => setFechaHasta(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            style={{ minWidth: 150 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={buscarCertificados}
                            disabled={modalLoading}
                        >
                            {modalLoading ? "Buscando..." : "Buscar"}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setFechaDesde(getDefaultFechaDesde());
                                setFechaHasta(getDefaultFechaHasta());
                            }}
                        >
                            Último año
                        </Button>
                    </Box>
                    {/* Buscador de texto */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar por certificado, autos, demandado..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ marginTop: 8 }}
                    />
                </DialogTitle>
                <DialogContent>
                    {modalLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer style={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox"></TableCell>
                                        <TableCell>Certif</TableCell>
                                        <TableCell>Autos</TableCell>
                                        <TableCell>Demandado</TableCell>
                                        <TableCell align="right">Costas</TableCell>
                                        <TableCell align="right">IVA</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {availableCertificados
                                        .filter((cert) =>
                                            !searchTerm ||
                                            cert.Numero.toString().includes(searchTerm) ||
                                            (cert.Expediente_Autos || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (cert.Demandado_NN || "").toLowerCase().includes(searchTerm.toLowerCase())
                                        )
                                        .map((cert, idx) => (
                                            <TableRow key={cert.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedToAdd.includes(cert.Id)}
                                                        onChange={() => handleToggleAdd(cert.Id)}
                                                    />
                                                </TableCell>
                                                <TableCell>{cert.Letra}-{cert.Numero}</TableCell>
                                                <TableCell>{cert.Expediente_Autos}</TableCell>
                                                <TableCell>{cert.Demandado_NN}</TableCell>
                                                <TableCell align="right">
                                                    $ {parseFloat(cert.Costas || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    $ {parseFloat(cert.Costas_IVA || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <Typography variant="body2" color="text.secondary" style={{ marginTop: 16 }}>
                        {selectedToAdd.length} certificado(s) seleccionado(s)
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleAñadirCertificados}
                        color="primary"
                        variant="contained"
                        disabled={selectedToAdd.length === 0 || processing}
                    >
                        {processing ? "Añadiendo..." : `Añadir ${selectedToAdd.length} certificado(s)`}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                message={notification.message}
            />
        </Box >
    );
};

export default SolicitudDetalle;
